import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { canAccess, MODULES } from '../../../../auth/permissions';
import { logError } from '../../../../utils/logger';
import { useAuth } from '../../../auth/auth.context';
import {
  BaseTextField,
  Card,
  ConfirmationModal,
  ControlledDatePicker,
  ControlledTextField,
  SectionTitle,
} from '../../../shared/components';
import SuccessModal from '../../../shared/components/feedback/SuccessModal';
import useAsyncData from '../../../shared/hooks/useAsyncData';
import ClientTypeField, { ClientType, DEFAULT_CLIENT_TYPE } from '../../components/ClientTypeField';
import { JOB_MASTERS, JobMasterKey } from '../../reports/masters.config';
import MasterSelectField from '../../reports/MasterSelectField';
import { useAddedMasterRows } from '../../reports/useAddedMasterRows';
import {
  createWorkJob,
  EMPTY_FILTER_OPTIONS,
  FilterOption,
  financialYearForDate,
  getWorkJobReportFilterOptions,
  WorkJobCreatePayload,
  WorkJobReportFilterOptions,
} from '../../reports/workJobReport.api';

/** The status a new job starts in — also the server's default when none is sent. */
const DEFAULT_STATUS_LABEL = 'Not Started';
/** The one status label the API pairs with a cancellation reason. */
const CANCELED_LABEL = 'Canceled';

const PROJECTS_PATH = '/work/projects';

interface AddProjectForm {
  companyId: number | null;
  jobCode: string;
  jobName: string;
  jobDate: dayjs.Dayjs | null;
  siteAddress: string;
  localityId: number | null;
  projectTypeId: number | null;
  constructionWorkTypeId: number | null;
  buildingTypeId: number | null;
  statusId: number | null;
  /** V36 — which of the three contacts below is the client. */
  clientType: ClientType;
  architectId: number | null;
  builderId: number | null;
  ownerId: number | null;
  cancellationReason: string;
}

const DEFAULTS: AddProjectForm = {
  companyId: null,
  jobCode: '',
  jobName: '',
  jobDate: null,
  siteAddress: '',
  localityId: null,
  projectTypeId: null,
  constructionWorkTypeId: null,
  buildingTypeId: null,
  statusId: null,
  clientType: DEFAULT_CLIENT_TYPE,
  architectId: null,
  builderId: null,
  ownerId: null,
  cancellationReason: '',
};

/**
 * Creates a job from exactly the fields the Work Job Report's Edit Job screen covers, laid
 * out the way New Job Creation lays out its steps. It is the short path for a job whose
 * quotation, floors, milestones and files will be filled in later — the seven-step wizard
 * is still the way to create one with all of that up front.
 *
 * Saves through the wizard's own `POST /api/analyst/job/create`, which needed no change:
 * it already accepts this subset and resolves the lookups by label, the same contract the
 * edit screen uses. Every master picker carries the same "+ Add" and per-row edit as the
 * edit screen, so a missing locality or contact never means leaving the form. The client is
 * not a separate contact (V36): it is whichever of the builder, architect and owner is chosen
 * last on the POC card.
 */
export default function AddProjectPage() {
  const navigate = useNavigate();

  const methods = useForm<AddProjectForm>({
    // Dated today by default; computed per mount so a tab left open overnight is not a day behind.
    defaultValues: { ...DEFAULTS, jobDate: dayjs() },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });
  const { control, handleSubmit, watch, setValue, getValues, setError, formState } = methods;

  const fetchOptions = useCallback(() => getWorkJobReportFilterOptions(), []);
  const {
    data: options,
    error: optionsError,
    refetch: refetchOptions,
  } = useAsyncData<WorkJobReportFilterOptions>(fetchOptions, EMPTY_FILTER_OPTIONS, {
    code: 'ADD_PROJECT_OPTIONS_FAILED',
    errorMessage: 'Failed to load the lists for this form',
  });

  const { withAdded, remember } = useAddedMasterRows();

  /** Only the admin may add or edit a company, so only the admin gets "+ Add" and the pencil. */
  const { role } = useAuth();
  const canEditCompanies = canAccess(role, MODULES.COMPANY_EDIT);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  // Start the job in "Not Started" once the statuses arrive, unless one was already picked.
  useEffect(() => {
    if (getValues('statusId') != null) return;
    const initial = options.statuses.find((status) => status.label === DEFAULT_STATUS_LABEL);
    if (initial) setValue('statusId', initial.id, { shouldDirty: false });
  }, [options.statuses, getValues, setValue]);

  const jobDate = watch('jobDate');
  const statusId = watch('statusId');

  // Same rule and same rows as the edit screen: the server derives the year from the date.
  const isoDate = jobDate && dayjs(jobDate).isValid() ? dayjs(jobDate).format('YYYY-MM-DD') : '';
  const financialYear = financialYearForDate(isoDate, options.financial_years);

  const statusLabel = options.statuses.find((status) => status.id === statusId)?.label;
  const isCanceling = statusLabel === CANCELED_LABEL;

  const labelFor = (list: FilterOption[], masterKey: JobMasterKey, id: number | null) =>
    id == null ? undefined : withAdded(list, masterKey).find((option) => option.id === id)?.label;

  /** The contact each client choice would name, from the pickers above it. */
  const [architectId, builderId, ownerId] = watch(['architectId', 'builderId', 'ownerId']);
  const contactNames = {
    ARCHITECT: labelFor(options.architects, 'architects', architectId),
    BUILDER: labelFor(options.builders, 'builders', builderId),
    OWNER: labelFor(options.owners, 'owners', ownerId),
  };

  const buildPayload = (values: AddProjectForm): WorkJobCreatePayload => {
    const payload: WorkJobCreatePayload = {
      job_code: values.jobCode.trim(),
      job_name: values.jobName.trim(),
      job_date: dayjs(values.jobDate).format('YYYY-MM-DD'),
      site_address: values.siteAddress.trim(),
      locality: labelFor(options.localities, 'localities', values.localityId) ?? '',
      milestone_count: 0,
    };

    // Only picked lookups travel. An absent project / construction work type lets the
    // server apply its defaults, rather than this screen guessing at one.
    const projectType = labelFor(options.project_types, 'projectTypes', values.projectTypeId);
    const workType = labelFor(
      options.construction_work_types,
      'constructionWorkTypes',
      values.constructionWorkTypeId
    );
    const buildingType = labelFor(options.building_types, 'buildingTypes', values.buildingTypeId);
    if (projectType) payload.job_type = projectType;
    if (workType) payload.job_work_type = workType;
    if (buildingType) payload.building_type = buildingType;

    if (statusLabel) payload.job_status = statusLabel;
    if (isCanceling) payload.cancellation_reason = values.cancellationReason.trim();

    if (values.companyId) payload.company_id = values.companyId;
    payload.client_type = values.clientType;
    if (values.architectId) payload.architect_id = values.architectId;
    if (values.builderId) payload.builder_id = values.builderId;
    if (values.ownerId) payload.owner_id = values.ownerId;

    return payload;
  };

  const onSubmit = async (values: AddProjectForm) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await createWorkJob(buildPayload(values));
      setCreatedCode(values.jobCode.trim());
    } catch (err: any) {
      // A duplicate job code is the one failure the user can fix in place — put it on
      // the field rather than in a banner at the bottom of the page.
      const codeError = err?.response?.data?.errors?.job_code;
      if (codeError) {
        setError('jobCode', { type: 'server', message: [].concat(codeError).join(' ') }, { shouldFocus: true });
      } else {
        setSubmitError(
          logError({ message: 'Failed to create the project', error: err, code: 'ADD_PROJECT_FAILED' })
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const leave = () => navigate(PROJECTS_PATH);

  const handleCancel = () => {
    if (formState.isDirty) setConfirmLeave(true);
    else leave();
  };

  /** One master picker, wired into the form and carrying its "+ Add" / edit popups. */
  const masterField = (
    name: keyof AddProjectForm,
    label: string,
    list: FilterOption[],
    masterKey?: JobMasterKey,
    required = false
  ) => (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: `${label} is required` } : undefined}
      render={({ field, fieldState }) => (
        <MasterSelectField
          label={label}
          value={field.value as number | null}
          options={withAdded(list, masterKey)}
          onChange={(id) => field.onChange(id)}
          master={masterKey ? JOB_MASTERS[masterKey] : undefined}
          required={required}
          labelPlacement='above'
          errorMessage={fieldState.error?.message}
          onMasterSaved={
            masterKey
              ? (row) => {
                  remember(masterKey, row);
                  refetchOptions();
                }
              : undefined
          }
        />
      )}
    />
  );

  return (
    <>
      <div className='column gap-3'>
        <SectionTitle title='Add Project' />

        {optionsError && (
          <Alert severity='error' action={<Button onClick={refetchOptions}>Retry</Button>}>
            {optionsError} — the pickers below will be empty until the lists load.
          </Alert>
        )}

        <FormProvider {...methods}>
          <form className='column gap-3' onSubmit={handleSubmit(onSubmit)} noValidate>
            <Card title='Basic Details'>
              <div className='grid grid--2-cols grid--with-divider'>
                <div className='column'>
                  <ControlledTextField
                    id='jobCode'
                    name='jobCode'
                    label='Job Code*'
                    placeholder='Enter job code'
                    rules={{
                      required: 'Job code is required',
                      maxLength: { value: 50, message: 'Job code cannot exceed 50 characters' },
                    }}
                  />
                  <ControlledTextField
                    id='jobName'
                    name='jobName'
                    label='Job Name*'
                    placeholder='Enter job name'
                    rules={{
                      required: 'Job name is required',
                      minLength: { value: 3, message: 'Job name should be at least 3 characters' },
                      maxLength: { value: 100, message: 'Job name cannot exceed 100 characters' },
                    }}
                  />
                  <ControlledDatePicker
                    id='jobDate'
                    name='jobDate'
                    label='Job Date*'
                    rules={{ required: 'Job date is required' }}
                  />
                  <BaseTextField
                    id='financialYear'
                    label='Financial Year'
                    value={financialYear?.label ?? ''}
                    placeholder='Set from the job date'
                    disabled
                    helperText='Derived from the job date — April to March.'
                  />
                </div>

                <div className='column'>
                  {masterField(
                    'companyId',
                    'Company',
                    options.companies,
                    canEditCompanies ? 'companies' : undefined,
                    true
                  )}
                  {masterField('localityId', 'Locality', options.localities, 'localities', true)}
                  <ControlledTextField
                    id='siteAddress'
                    name='siteAddress'
                    label='Site Address*'
                    placeholder='Enter site address'
                    isMultiline
                    rows={3}
                    rules={{
                      required: 'Site address is required',
                      minLength: { value: 3, message: 'Site address should be at least 3 characters' },
                      maxLength: { value: 300, message: 'Site address cannot exceed 300 characters' },
                    }}
                  />
                </div>
              </div>
            </Card>

            <Card title='Type of Work'>
              <div className='grid grid--2-cols grid--with-divider'>
                <div className='column'>
                  {masterField('projectTypeId', 'Project Type', options.project_types, 'projectTypes')}
                  {masterField(
                    'constructionWorkTypeId',
                    'Construction Work Type',
                    options.construction_work_types,
                    'constructionWorkTypes'
                  )}
                </div>
                <div className='column'>
                  {masterField('buildingTypeId', 'Building Type', options.building_types, 'buildingTypes')}
                </div>
              </div>
            </Card>

            <Card title='POC'>
              <div className='grid grid--2-cols grid--with-divider'>
                <div className='column'>
                  {masterField('architectId', 'Architect', options.architects, 'architects')}
                  {masterField('builderId', 'Builder', options.builders, 'builders')}
                </div>
                <div className='column'>
                  {masterField('ownerId', 'Owner', options.owners, 'owners')}
                  {/* Last: the client is one of the three contacts above (V36). */}
                  <Controller
                    name='clientType'
                    control={control}
                    rules={{
                      validate: (type) =>
                        Boolean(contactNames[type]) || `Pick the job's ${type.toLowerCase()} first, or choose another client.`,
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <ClientTypeField
                          value={field.value}
                          onChange={field.onChange}
                          names={contactNames}
                          labelPlacement='above'
                        />
                        {fieldState.error && (
                          <Typography variant='caption' color='error'>{fieldState.error.message}</Typography>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>
            </Card>

            <Card title='Job Status'>
              <div className='grid grid--2-cols grid--with-divider'>
                <div className='column'>
                  {/* No master key: statuses carry workflow meaning and are not added ad hoc. */}
                  {masterField('statusId', 'Status', options.statuses)}
                </div>
                <div className='column'>
                  {isCanceling && (
                    <ControlledTextField
                      id='cancellationReason'
                      name='cancellationReason'
                      label='Cancellation Reason*'
                      placeholder='Why is this job canceled?'
                      isMultiline
                      rows={3}
                      rules={{ required: 'A cancellation reason is required for a canceled job' }}
                    />
                  )}
                </div>
              </div>
            </Card>

            {submitError && <Alert severity='error'>{submitError}</Alert>}

            <div className='row row--between'>
              <button type='button' className='btn text-btn' onClick={handleCancel} disabled={submitting}>
                Cancel
              </button>
              <button type='submit' className='btn cta-btn' disabled={submitting}>
                {submitting ? 'Saving…' : 'Save Project'}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>

      <ConfirmationModal
        open={confirmLeave}
        title='Discard this project?'
        message={
          <Typography sx={{ fontSize: '1rem', color: 'grey.500', textAlign: 'center' }}>
            The details entered here have not been saved.
          </Typography>
        }
        onClose={() => setConfirmLeave(false)}
        onConfirm={leave}
        confirmText='Discard'
        cancelText='Keep editing'
      />

      <SuccessModal
        open={Boolean(createdCode)}
        onClose={leave}
        message={`Project ${createdCode ?? ''} created successfully.`}
        maxWidth='sm'
      />
    </>
  );
}
